import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Droplet, Plus, Minus } from "lucide-react";
import { api } from "@/lib/api";

export function WaterCard({ glasses = 0, goal = 8, onChange }) {
  const [count, setCount] = useState(glasses);
  const [busy, setBusy] = useState(false);

  useEffect(() => { setCount(glasses); }, [glasses]);

  const change = async (delta) => {
    if (busy) return;
    const optimistic = Math.max(0, count + delta);
    setCount(optimistic);
    setBusy(true);
    try {
      const r = await api.logWater(delta);
      setCount(r.glasses);
      onChange?.(r.glasses);
    } catch (e) {
      setCount(count); // revert
    } finally {
      setBusy(false);
    }
  };

  const size = 72;
  const stroke = 8;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const pct = Math.min(1, goal > 0 ? count / goal : 0);

  return (
    <div className="rounded-3xl bg-card border border-border p-4 flex items-center gap-4" data-testid="water-card">
      <div className="relative shrink-0" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="-rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="hsl(var(--muted))" strokeWidth={stroke} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#3B9BD6" strokeWidth={stroke}
            strokeLinecap="round" strokeDasharray={circ} strokeDashoffset={circ * (1 - pct)}
            style={{ transition: "stroke-dashoffset 0.6s cubic-bezier(0.22,1,0.36,1)" }}
          />
        </svg>
        <motion.div key={count} initial={{ scale: 0.7 }} animate={{ scale: 1 }} className="absolute inset-0 flex items-center justify-center">
          <Droplet className="w-6 h-6" style={{ color: "#3B9BD6", fill: pct >= 1 ? "#3B9BD6" : "transparent" }} />
        </motion.div>
      </div>

      <div className="flex-1">
        <div className="font-semibold text-sm">Hydration</div>
        <div className="text-xs text-muted-foreground">
          <span className="text-base font-display font-medium text-foreground" data-testid="water-count">{count}</span> / {goal} glasses
        </div>
      </div>

      <div className="flex items-center gap-2">
        <button
          onClick={() => change(-1)}
          disabled={busy || count === 0}
          data-testid="water-minus"
          className="w-9 h-9 rounded-full bg-secondary flex items-center justify-center text-foreground/70 disabled:opacity-40 active:scale-90 transition-transform"
        >
          <Minus className="w-4 h-4" />
        </button>
        <button
          onClick={() => change(1)}
          disabled={busy}
          data-testid="water-plus"
          className="h-9 pl-3 pr-4 rounded-full text-white flex items-center gap-1 font-semibold text-sm active:scale-95 transition-transform"
          style={{ background: "#3B9BD6" }}
        >
          <Plus className="w-4 h-4" /> Glass
        </button>
      </div>
    </div>
  );
}
